import React, {lazy, Suspense} from 'react';
import {Navigate, Route, Routes} from 'react-router-dom';
import {LoadingSpinner} from '@components/common/LoadingSpinner';
import {Layout} from '@components/layout/Layout';
import {useAuth} from '@hooks/useAuth';
import {Login} from '@components/auth/Login';

// Lazy load pages for code splitting
const Dashboard = lazy(() => import('@pages/Dashboard'));
const Workers = lazy(() => import('@pages/Workers'));
const Accounts = lazy(() => import('@pages/Accounts'));
const Schedule = lazy(() => import('@pages/Schedule'));
const Videos = lazy(() => import('@pages/Videos'));
const Settings = lazy(() => import('@pages/Settings'));
const NotFound = lazy(() => import('@pages/NotFound'));

// Protected route wrapper with authentication
interface ProtectedRouteProps {
  children: React.ReactNode;
  withLayout?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, withLayout = true }) => {
    const {isAuthenticated, isLoading} = useAuth();

    if (isLoading) {
        return <LoadingSpinner fullScreen message="Checking authentication..."/>;
    }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return withLayout ? <Layout>{children}</Layout> : <>{children}</>;
};

// Main routing component
export const AppRoutes: React.FC = () => {
  return (
    <Suspense fallback={<LoadingSpinner fullScreen message="Loading page..." />}>
      <Routes>
        {/* Public routes */}
          <Route path="/login" element={<Login/>}/>

        {/* Protected routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/workers/*"
          element={
            <ProtectedRoute>
              <Workers />
            </ProtectedRoute>
          }
        />
        <Route
          path="/accounts/*"
          element={
            <ProtectedRoute>
              <Accounts />
            </ProtectedRoute>
          }
        />
        <Route
          path="/schedule/*"
          element={
            <ProtectedRoute>
              <Schedule />
            </ProtectedRoute>
          }
        />
        <Route
          path="/videos/*"
          element={
            <ProtectedRoute>
              <Videos />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/*"
          element={
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          }
        />

        {/* 404 Not Found */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};

// Route configuration for navigation
// Moved to routes/config.ts to satisfy react-refresh rule
/*
export const routeConfig = [
    {
        path: '/',
        name: 'Dashboard',
        icon: 'Dashboard',
    },
    {
        path: '/workers',
        name: 'Workers',
        icon: 'Engineering',
    },
    {
        path: '/accounts',
        name: 'Accounts',
        icon: 'AccountCircle',
    },
    {
        path: '/schedule',
        name: 'Schedule',
        icon: 'Schedule',
    },
    {
        path: '/videos',
        name: 'Videos',
        icon: 'VideoLibrary',
    },
    {
        path: '/settings',
        name: 'Settings',
        icon: 'Settings',
    },
];
*/
